<template>
  <Transition name="slide-fade" appear>
    <div v-if="visible" class="modal-end">
      <div class="modal-content">
        <div class="modal-header">
          <span class="modal-header-txt">{{ $t('assign_agent') }}</span>
        </div>

        <div class="search-wrapper">
          <img src="@/assets/images/search.svg" />
          <input class="search-input" type="text" :placeholder="$t('search_type')" v-model="searchText" v-on:keyup.enter="onSearch"/>
          <div v-if="searchText" class="searchBarCross" @click="resetSearch">
            <img class="cross" src="../../assets/images/cross.svg" />
          </div>
        </div>

        <div class="program-list scroll-design" v-scroll-end="loadMore">
          <div
              class="program-row"
              v-for="item in agents"
              :key="item.id"
              @click="() => toggleAgent(item.id)"
              :class="{ 'checked': isSelected(item.id) }"
          >
            <div class="program-left">
              <img
                  v-if="!isSelected(item.id)"
                  src="@/assets/images/checkbox-circle.svg"
              />
              <img
                  v-else
                  src="@/assets/images/checkboxchecked-circle.svg"
              />
              <img v-if="item.avatarUrl" :src="item.avatarUrl" class="agent-avatar" />
              <div class="program-info">
                <span class="program-title">{{ getAgentName(item.name) }}</span>
                <span class="program-text">{{ item.description }}</span>
              </div>
            </div>

            <div class="program-right">
              <div class="status-tag" :class="item.status">
                <div class="status-dot" :class="item.status"></div>
                <span>{{ item.status }}</span>
              </div>
            </div>
          </div>

          <div v-if="isLoading" class="loading-center">
            <VueSpinner size="32" />
          </div>
        </div>

        <button class="dark-button w-100 mt-2" @click="onSave">
          <span v-if="!saveLoading">{{ $t('assign') }}</span>
          <VueSpinnerIos v-if="saveLoading" size="20" height="100%" color="#fff" />
        </button>

        <div @click="handleClose" class="close-btn">
          <img class="cross" src="../../assets/images/cross.svg" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { VueSpinner, VueSpinnerIos } from 'vue3-spinners';
import agentService from "@/service/agentService";
import { handleErrorResponse } from "@/utils/utils";

export default {
  components: {
    VueSpinner,
    VueSpinnerIos,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    onHide: {
      type: Function,
      default: () => {},
    },
    companyId: {
      type: String,
      default: '',
    },
    userId: {
      type: String,
      default: '',
    },
    departmentId: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'user',
    },
    refreshCallback: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      searchText: '',
      isLoading: false,
      agents: [],
      currentPage: 1,
      lastPage: false,
      selectedIds: [],
      originalAssignedIds: [],
      saveLoading: false,
    };
  },

  computed: {
    currentLocale() {
      return localStorage.getItem('userLanguage') ||
          localStorage.getItem('language') ||
          localStorage.getItem('locale') ||
          this.$i18n?.locale ||
          'tr';
    },
  },

  watch: {
    visible: {
      handler(isVisible) {
        if (isVisible && this.companyId) {
          this.agents = [];
          this.selectedIds = [];
          this.originalAssignedIds = [];
          this.searchText = '';
          this.currentPage = 1;
          this.lastPage = false;
          this.loadAssignedAgents().then(() => {
            this.loadAllAgents();
          });
        }
      },
    },
  },

  methods: {
    getAgentName(name) {
      if (!name) return '-';
      if (typeof name === 'string') return name;
      return name[this.currentLocale] || name['tr'] || name['en'] || Object.values(name)[0] || '-';
    },

    async loadAssignedAgents() {
      this.isLoading = true;
      try {
        const params = { companyId: this.companyId, limit: 100 };

        if (this.type === 'user' && this.userId) {
          params.userId = this.userId;
          params.type = 'user';
        } else if (this.type === 'department' && this.departmentId) {
          params.departmentId = this.departmentId;
        }

        const response = await agentService.getAgentsByAssignment(params);
        if (response?.data?.success) {
          const assigned = response.data.agents || [];
          this.originalAssignedIds = assigned.map(a => a.id);
          this.selectedIds = [...this.originalAssignedIds];
          this.agents = [...assigned];
        }
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      }
      this.isLoading = false;
    },

    async loadAllAgents() {
      this.isLoading = true;
      this.currentPage = 1;
      this.lastPage = false;

      try {
        const params = { companyId: this.companyId, page: this.currentPage, limit: 20 };
        if (this.searchText) {
          params.searchText = this.searchText;
        }

        const response = await agentService.getAgentsByAssignment(params);
        if (response?.data?.success) {
          const allAgents = response.data.agents || [];

          if (this.searchText) {
            this.agents = allAgents;
          } else {
            const existingIds = this.agents.map(a => a.id);
            const newAgents = allAgents.filter(a => !existingIds.includes(a.id));
            this.agents = [...this.agents, ...newAgents];
          }

          if (response.data.pagination) {
            this.lastPage = response.data.pagination.currentPage >= response.data.pagination.totalPages;
          }
        }
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      }
      this.isLoading = false;
    },

    loadMore() {
      if (!this.lastPage && !this.isLoading) {
        this.currentPage++;
        this.loadMoreAgents();
      }
    },

    async loadMoreAgents() {
      this.isLoading = true;
      try {
        const params = { companyId: this.companyId, page: this.currentPage, limit: 20 };
        if (this.searchText) {
          params.searchText = this.searchText;
        }

        const response = await agentService.getAgentsByAssignment(params);
        if (response?.data?.success) {
          const newAgents = response.data.agents || [];
          this.agents = [...this.agents, ...newAgents];

          if (response.data.pagination) {
            this.lastPage = response.data.pagination.currentPage >= response.data.pagination.totalPages;
          }
        }
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      }
      this.isLoading = false;
    },

    toggleAgent(id) {
      if (this.selectedIds.includes(id)) {
        this.selectedIds = this.selectedIds.filter(i => i !== id);
      } else {
        this.selectedIds.push(id);
      }
    },

    isSelected(id) {
      return this.selectedIds.includes(id);
    },

    onSearch() {
      this.agents = [];
      this.currentPage = 1;
      this.lastPage = false;
      this.loadAllAgents();
    },

    resetSearch() {
      this.searchText = '';
      this.agents = [];
      this.currentPage = 1;
      this.lastPage = false;
      this.loadAssignedAgents().then(() => {
        this.loadAllAgents();
      });
    },

    async onSave() {
      const newIds = this.selectedIds.filter(id => !this.originalAssignedIds.includes(id));
      const removedIds = this.originalAssignedIds.filter(id => !this.selectedIds.includes(id));

      if (newIds.length === 0 && removedIds.length === 0) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('no_changes_to_save'),
        });
        return;
      }

      this.saveLoading = true;

      try {
        const promises = [];

        // Assign new agents
        for (const agentId of newIds) {
          const body = { agentId, companyId: this.companyId };
          if (this.type === 'user' && this.userId) {
            body.userId = this.userId;
          } else if (this.type === 'department' && this.departmentId) {
            body.departmentId = this.departmentId;
          }
          promises.push(agentService.assignAgent(body));
        }

        // Unassign removed agents
        for (const agentId of removedIds) {
          const body = { agentId, companyId: this.companyId };
          if (this.type === 'user' && this.userId) {
            body.userId = this.userId;
          } else if (this.type === 'department' && this.departmentId) {
            body.departmentId = this.departmentId;
          }
          promises.push(agentService.unassignAgent(body));
        }

        await Promise.all(promises);

        let message = '';
        if (newIds.length > 0 && removedIds.length > 0) {
          message = this.$t('agents_updated_successfully', { added: newIds.length, removed: removedIds.length });
        } else if (newIds.length > 0) {
          message = this.$t('agents_assigned_successfully', { count: newIds.length });
        } else if (removedIds.length > 0) {
          message = this.$t('agents_removed_successfully', { count: removedIds.length });
        }

        this.$snackbar.add({
          type: 'success',
          text: message,
        });

        this.refreshCallback();
        this.onHide();
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      }
      this.saveLoading = false;
    },

    handleClose() {
      this.onHide();
      this.searchText = '';
      this.agents = [];
      this.selectedIds = [];
      this.originalAssignedIds = [];
    },
  },
};
</script>

<style scoped>
.searchBarCross {
  margin-right: 5px;
}

.modal-content {
  position: relative;
  background-color: white;
  width: 50%;
  height: 100%;
  border-radius: 12px;
  padding: 24px;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
}

.modal-content::-webkit-scrollbar {
  width: 15px;
}

.modal-content::-webkit-scrollbar-track {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  background-color: #FFF;
}

.modal-content::-webkit-scrollbar-thumb {
  background-color: rgba(4, 12, 45, 0.2);
  border-radius: 10px;
  border: 5px transparent solid;
  background-clip: padding-box;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(4, 12, 45, 0.4);
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  border: none;
  cursor: pointer;
  background-color: #F2F4F7;
  border-radius: 3000px;
}

.cross {
  padding: 8px;
}

.modal-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 0 10px 0;
  border: none;
}

.modal-header-txt {
  font-family: euclid_semi_bold, sans-serif;
  color: #040C2D;
  font-size: 18px;
  font-style: normal;
  line-height: 24px;
}

.search-wrapper {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 1px solid #EAECF0;
  background-color: #F9FAFB;
  border-radius: 24px;
  margin-bottom: 20px;
}

.search-wrapper img {
  margin-left: 10px;
}

.search-input {
  border: none;
  outline: none;
  background-color: transparent;
  padding: 10px;
  width: 100%;
}

.program-list {
  height: 100%;
  overflow-x: auto;
  padding-left: 15px;
  padding-right: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.agent-avatar {
  height: 48px;
  width: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.program-row {
  display: flex;
  flex-direction: row;
  gap: 15px;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  border: 1px solid transparent;
  cursor: pointer;
}

.program-row.checked {
  border: 1px solid #3DA33E;
  background-color: #f5f5f5;
  border-radius: 16px;
}

.program-left {
  display: flex;
  flex-direction: row;
  gap: 15px;
  align-items: center;
}

.program-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.program-info .program-title {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #101828;
}

.program-info .program-text {
  font-size: 12px;
  color: #667085;
}

.program-right {
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
}

.status-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-family: euclid_medium, sans-serif;
}

.status-tag.published {
  background-color: #ECFDF3;
  color: #027A48;
}

.status-tag.draft {
  background-color: #F2F4F7;
  color: #344054;
}

.status-tag.training {
  background-color: #FFF4ED;
  color: #B93815;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-dot.published {
  background-color: #12B76A;
}

.status-dot.draft {
  background-color: #667085;
}

.status-dot.training {
  background-color: #F79009;
}

.loading-center {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}
</style>
